import {
  getFindAllVenueRentalConfigurationsQueryKey,
  useDeleteVenueRentalConfiguration,
} from '@api';
import { useInvalidateQuery, useNotification } from '@common';
import { modals } from '@mantine/modals';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const TRANS_PATH = 'RENTAL_CONFIGURATION_TABLE';
const TRANS_PATH_NOTIFICATION = 'NOTIFICATIONS.DELETE';

type UseDeleteRentalConfigurationArgsType = {
  venueId: string;
  version: string;
};

type UseDeleteRentalConfigurationReturnType = {
  handleDelete: (configurationId: string) => void;
};

export const useDeleteRentalConfiguration = ({
  venueId,
  version,
}: UseDeleteRentalConfigurationArgsType): UseDeleteRentalConfigurationReturnType => {
  const { t } = useTranslation('venueDetailsRental');
  const { showError, showSuccess } = useNotification();
  const { mutate: mutateDelete } = useDeleteVenueRentalConfiguration();
  const { invalidateQuery } = useInvalidateQuery(
    getFindAllVenueRentalConfigurationsQueryKey(venueId, version).filter(
      Boolean
    )
  );

  const onSuccess = (): void => {
    invalidateQuery();
    showSuccess({
      title: `${TRANS_PATH_NOTIFICATION}.SUCCESS.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.SUCCESS.MESSAGE`,
    });
  };

  const onError = (error: AxiosError): void => {
    showError({
      title: `${TRANS_PATH_NOTIFICATION}.ERROR.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.ERROR.MESSAGE`,
      error: error,
    });
  };

  const onConfirm = (configurationId: string): void => {
    mutateDelete(
      {
        venueId,
        configurationId,
      },
      {
        onSuccess,
        onError,
      }
    );
  };

  const handleDelete = (configurationId: string): void => {
    modals.openConfirmModal({
      title: t(`${TRANS_PATH}.DELETE_CONFIRMATION_TITLE`),
      labels: {
        confirm: t(`${TRANS_PATH}.DELETE_CONFIRMATION_BUTTON`),
        cancel: t('CANCEL_BUTTON', { ns: 'common' }),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () => onConfirm(configurationId),
    });
  };

  return { handleDelete };
};
